/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * MockResolver
 *
 * Registry entry for the `mock` engine. Combines the in-process mock LSP
 * (for the mock engine's hardcoded keywords + demo tables, mapped via
 * `mockLspBridge`) with a direct schema-bus consultation that surfaces
 * fixture-injected tables and columns as Monaco completion items.
 *
 * When a future slice ships a native tree-sitter mock resolver, this module
 * is the seam to extend: replace the bridge call + bus consult with a richer
 * resolver pipeline (`deriveCompletionContext` + `rankCandidates`).
 */

import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';

import { fetchMockLspSuggestions } from './mockLspBridge';
import type { OrchestratorResolver, OrchestratorResolverInput } from './types';

export const mockResolver: OrchestratorResolver = {
  engineId: 'mock',
  resolve: async (input) => {
    const lspSuggestions = await fetchMockLspSuggestions(input);
    const busSuggestions = buildBusSuggestions(input);
    if (busSuggestions.length === 0) return { suggestions: lspSuggestions };

    const merged = dedupeByLabelLower([...lspSuggestions, ...busSuggestions]);
    return { suggestions: merged };
  },
};

function buildBusSuggestions(input: OrchestratorResolverInput): any[] {
  if (!input.connectionId) return [];
  const snap = getSchemaBusSnapshot(input.connectionId, input.database);
  if (!snap) return [];

  const { model, position, monaco } = input;
  const wordInfo = model.getWordUntilPosition(position);
  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };

  const CompletionItemKind = monaco?.languages?.CompletionItemKind;
  const tableKind = CompletionItemKind?.Variable ?? 6;
  const columnKind = CompletionItemKind?.Field ?? 5;

  const out: any[] = [];
  let order = 0;
  for (const table of snap.tables) {
    out.push({
      label: table.name,
      kind: tableKind,
      detail: `Table: ${table.schema ? `${table.schema}.` : ''}${table.name}`,
      insertText: table.name,
      sortText: `2_${String(order++).padStart(5, '0')}_${table.name}`,
      filterText: table.name,
      range,
    });
    for (const col of table.columns) {
      out.push({
        label: col.name,
        kind: columnKind,
        detail: `Column: ${col.dataType} (${table.name})`,
        insertText: col.name,
        sortText: `1_${String(order++).padStart(5, '0')}_${col.name}`,
        filterText: col.name,
        range,
      });
    }
  }
  return out;
}

function dedupeByLabelLower(items: readonly any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const item of items) {
    const rawLabel = typeof item.label === 'string' ? item.label : (item.label?.label ?? '');
    const key = rawLabel.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}
